import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const links = (t) => [
  { to: "/", label: t.nav.home, id: "nav-home" },
  { to: "/about", label: t.nav.about, id: "nav-about" },
  { to: "/services", label: t.nav.services, id: "nav-services" },
  { to: "/digital", label: t.nav.digital, id: "nav-digital" },
  { to: "/contact", label: t.nav.contact, id: "nav-contact" },
];

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 12,
  );
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-200/70"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <span className="inline-flex items-center justify-center w-10 h-10 bg-[#0A192F] text-[#D4AF37] font-serif text-xl tracking-tight">
            M
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-lg text-[#0A192F] tracking-tight">
              Mercurio
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-gray-500">
              Advisors Group
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links(t).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide uppercase transition-colors ${
                  isActive
                    ? "text-[#0A192F]"
                    : "text-gray-500 hover:text-[#0A192F]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-switcher"
            onClick={toggle}
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0A192F] hover:text-[#D4AF37] transition-colors px-3 py-2 border border-gray-200 hover:border-[#D4AF37]"
            aria-label="Toggle language"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "EN / ES" : "ES / EN"}
          </button>

          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center bg-[#0A192F] text-white px-5 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#D4AF37] hover:text-[#0A192F] transition-colors"
          >
            {t.nav.cta}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#0A192F]"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-6 flex flex-col gap-4">
            {links(t).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`m-${l.id}`}
                className={({ isActive }) =>
                  `text-base font-medium tracking-wide uppercase ${
                    isActive ? "text-[#0A192F]" : "text-gray-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="m-nav-cta"
              className="mt-2 inline-flex items-center justify-center bg-[#0A192F] text-white px-5 py-3 text-xs uppercase tracking-[0.2em]"
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
